import {createSelector} from 'reselect';

import {
    getListing,
    getListingPage,
    getTranslations,
    getTripData,
    getTravelerFeatures
} from './store';
import {getTripNights} from './tripNights';
import {getCurrency, getIsMobile, getLocaleJS} from './system';
import {labelMaker} from '../../server/utils/labelMaker';

const getMultiUnitsLayoutValues = createSelector(
    [getListing, getListingPage],
    (listing, listingPage) => {
        return {
            isMultiUnit: listing.multiUnitProperty,
            showMultiUnits: listingPage.showMultiUnits && listing.multiUnitProperty,
            isHotelType: listing.propertyTypeKey === 'hotel',
            listingId: listing.listingId
        };
    }
);

const getLabels = createSelector(
    [getTranslations, getMultiUnitsLayoutValues, getLocaleJS, getTripNights],
    (translations, multiUnitsLayoutValues, localeJs, tripNights) => {
        const {isHotelType} = multiUnitsLayoutValues;
        const {
            areaLabel,
            areaLabelVerbose,
            averageNightly,
            bathroomsFilterLabel,
            instantConfirmationSecondaryLabel,
            bathroomsFullAbbreviationParam,
            bathroomsHalfAbbreviation,
            bathroomsHalfLabel,
            bathroomsToiletOnlyAbbreviation,
            bathroomsToiletOnlyLabel,
            bedroomsAbbreviationParam,
            bedroomsNum,
            multipleUnitsErrorDescription,
            multipleUnitsErrorTitle,
            nightTotal,
            noReviews,
            perNight,
            propertyType,
            ratesNone,
            ratingScreenReaderLabel,
            sleepsAbbreviation,
            tempOopsMessage,
            unavailableForDates,
            unavailableForGuests,
            viewOtherUnits,
            viewOtherRooms,
            viewMoreUnits,
            otherUnitsAvailable,
            otherRoomsAvailable,
            otherUnitsHeadlineHotel,
            otherUnitsHeadlineProperty
        } = translations;
        const evaluatedLabels = isHotelType
            ? {
                  viewOther: viewOtherRooms,
                  otherUnitsAvailable: otherRoomsAvailable,
                  otherUnitsHeadline: otherUnitsHeadlineHotel
              }
            : {
                  viewOther: viewOtherUnits,
                  otherUnitsAvailable,
                  otherUnitsHeadline: otherUnitsHeadlineProperty
              };
        evaluatedLabels.nightTotal = labelMaker(nightTotal, tripNights);
        return {
            locale: localeJs,
            messages: {
                areaLabel,
                areaLabelVerbose,
                averageNightly,
                bathroomsFilterLabel,
                instantConfirmationSecondaryLabel,
                bathroomsFullAbbreviationParam,
                bathroomsHalfAbbreviation,
                bathroomsHalfLabel,
                bathroomsToiletOnlyAbbreviation,
                bathroomsToiletOnlyLabel,
                bedroomsAbbreviationParam,
                bedroomsFilterLabel: bedroomsNum,
                multipleUnitsErrorDescription,
                multipleUnitsErrorTitle,
                noReviews,
                perNight,
                propertyType,
                ratesNone,
                ratingScreenReaderLabel,
                sleepsAbbreviation,
                tempOopsMessage,
                unavailableForDates,
                unavailableForGuests,
                viewMoreUnits,
                ...evaluatedLabels
            }
        };
    }
);

const getUnitsHeadlineContainerProps = createSelector(
    [getLabels, getMultiUnitsLayoutValues],
    (labels, multiUnitLayoutValues) => {
        return {
            ...labels,
            ...multiUnitLayoutValues
        };
    }
);

const getUnitsCollectionContainerProps = createSelector(
    [
        getLabels,
        getMultiUnitsLayoutValues,
        getTripData,
        getIsMobile,
        getCurrency,
        getTravelerFeatures
    ],
    (labels, multiUnitsLayoutValues, tripData, isMobile, currency, travelerFeatures) => {
        const defaultAdults = 1;
        return {
            ...multiUnitsLayoutValues,
            ...labels,
            isMobile,
            travelerFeatures,
            tripParams: {
                adults: tripData.adults || defaultAdults,
                children: tripData.children,
                petIncluded: tripData.hasPets,
                arrivalDate: tripData.arriveDateText,
                departureDate: tripData.departDateText,
                currency
            }
        };
    }
);

export {
    getMultiUnitsLayoutValues,
    getUnitsHeadlineContainerProps,
    getUnitsCollectionContainerProps
};
